/**
 * Saldos y patrimonio.
 *
 * PRINCIPIO: **el saldo de una cuenta nunca se guarda, siempre se calcula** a
 * partir de sus movimientos más el saldo inicial. Ese único cambio elimina la
 * familia de fallos que sufría Cuentia: allí el saldo se modificaba a mano, así
 * que **borrar un gasto dejaba el saldo descontado para siempre** y el descuadre
 * se acumulaba sin que nadie lo notara.
 *
 * Y la corrección contable: los abonos a deudas y los pagos de fiado **sí** mueven
 * la caja (antes no lo hacían, e inflaban el saldo disponible y el patrimonio).
 */
import { cashDelta as computeCashDelta, counterAccountDelta as computeCounterDelta, } from './transactions-kind.js';
/** Las cuentas cuyo saldo es dinero a favor. */
const ASSET_KINDS = new Set([
    'CASH',
    'SAVINGS',
    'WALLET',
    'INVESTMENT',
]);
/** Las cuentas cuyo saldo representa una deuda. */
const LIABILITY_KINDS = new Set([
    'CREDIT_CARD',
    'LOAN',
    'FIADO',
]);
/** ¿El saldo de esta cuenta es dinero a favor? */
export function isAssetAccount(kind) {
    return ASSET_KINDS.has(kind);
}
/** ¿El saldo de esta cuenta es una deuda? */
export function isLiabilityAccount(kind) {
    return LIABILITY_KINDS.has(kind);
}
/**
 * Efecto con signo de un movimiento sobre el saldo de **una** cuenta concreta.
 * Tiene en cuenta tanto la cuenta de origen como la de destino.
 */
export function effectOnAccount(accountId, tx) {
    const input = { kind: tx.kind, amount: tx.amount, direction: tx.direction };
    const fromOrigin = tx.accountId === accountId ? computeCashDelta(input) : 0;
    const fromDestination = tx.toAccountId === accountId ? computeCounterDelta(input) : 0;
    return fromOrigin + fromDestination;
}
/**
 * Calcula el saldo real de **todas** las cuentas.
 * Devuelve un mapa `id → saldo`.
 */
export function computeAccountBalances(accounts, transactions) {
    const balances = new Map();
    for (const account of accounts) {
        balances.set(account.id, account.initialBalance);
    }
    for (const tx of transactions) {
        if (tx.accountId) {
            const current = balances.get(tx.accountId);
            if (current !== undefined) {
                balances.set(tx.accountId, current + computeCashDelta(tx));
            }
        }
        if (tx.toAccountId) {
            const current = balances.get(tx.toAccountId);
            if (current !== undefined) {
                balances.set(tx.toAccountId, current + computeCounterDelta(tx));
            }
        }
    }
    return balances;
}
/** Saldo de una sola cuenta. */
export function computeAccountBalance(account, transactions) {
    let balance = account.initialBalance;
    for (const tx of transactions) {
        balance += effectOnAccount(account.id, tx);
    }
    return balance;
}
/**
 * Calcula el estado de una tarjeta.
 *
 * OJO: un gasto hecho con tarjeta deja el saldo en negativo, y ese negativo es
 * la deuda. En Cuentia, el pasivo solo sumaba los saldos **positivos**, así que
 * **la deuda de la tarjeta desaparecía del balance**.
 */
export function creditCardState(account, balance) {
    const usedCredit = Math.max(0, -balance);
    const limit = account.creditLimit ?? null;
    if (limit === null || limit <= 0) {
        return { usedCredit, availableCredit: null, usagePercent: null };
    }
    return {
        usedCredit,
        availableCredit: Math.max(0, limit - usedCredit),
        usagePercent: Math.min(100, (usedCredit / limit) * 100),
    };
}
/**
 * Calcula el patrimonio neto.
 *
 * REGLA ANTIDOBLE CONTEO: si una cuenta ya está representada por una deuda
 * formal o por una tienda de fiado (`accountId`), se excluye del cálculo por
 * cuentas. Así el mismo dinero nunca se cuenta dos veces.
 */
export function computeNetWorth(input) {
    const { accounts, transactions, debts = [], fiadoStores = [] } = input;
    const balances = computeAccountBalances(accounts, transactions);
    // Cuentas que ya están representadas por una deuda o por un fiado.
    const represented = new Set();
    for (const debt of debts)
        if (debt.accountId)
            represented.add(debt.accountId);
    for (const store of fiadoStores)
        if (store.accountId)
            represented.add(store.accountId);
    let assets = 0;
    let liabilities = 0;
    const byAccount = [];
    for (const account of accounts) {
        const balance = balances.get(account.id) ?? account.initialBalance;
        if (represented.has(account.id)) {
            byAccount.push({ accountId: account.id, kind: account.kind, balance, role: 'excluded' });
            continue;
        }
        if (isAssetAccount(account.kind)) {
            assets += balance;
            byAccount.push({ accountId: account.id, kind: account.kind, balance, role: 'asset' });
        }
        else if (isLiabilityAccount(account.kind)) {
            // En una cuenta de pasivo, lo que se debe es el saldo negativo.
            const owed = Math.max(0, -balance);
            liabilities += owed;
            byAccount.push({ accountId: account.id, kind: account.kind, balance: owed, role: 'liability' });
        }
    }
    const debtsTotal = debts.reduce((acc, debt) => acc + Math.max(0, debt.balance), 0);
    const fiadoTotal = fiadoStores.reduce((acc, store) => acc + Math.max(0, store.balance), 0);
    liabilities += debtsTotal + fiadoTotal;
    return {
        assets,
        liabilities,
        net: assets - liabilities,
        byAccount,
        debtsTotal,
        fiadoTotal,
    };
}
/** Saldo disponible para gastar: solo las cuentas de dinero a favor. */
export function availableCash(accounts, transactions) {
    const balances = computeAccountBalances(accounts, transactions);
    let total = 0;
    for (const account of accounts) {
        if (isAssetAccount(account.kind)) {
            total += balances.get(account.id) ?? account.initialBalance;
        }
    }
    return total;
}
/**
 * Cuánto de lo que hay en las cuentas de dinero a favor está reservado para
 * metas de ahorro. Sirve para que el saldo disponible no engañe: el dinero de
 * una meta no es dinero para gastar.
 */
export function reservedForGoals(goals) {
    return goals
        .filter((goal) => goal.status === 'ACTIVE' || goal.status === 'PAUSED')
        .reduce((acc, goal) => acc + Math.max(0, goal.savedAmount), 0);
}
/** Dinero disponible de verdad = saldo a favor − lo reservado para metas. */
export function spendableCash(accounts, transactions, goals = []) {
    return availableCash(accounts, transactions) - reservedForGoals(goals);
}
//# sourceMappingURL=accounts.js.map